import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import InvoiceItemsTable from '../invoices/InvoiceItemsTable';
import AccountSelectModal from './AccountSelectModal';
import ProductSelectModal from './ProductSelectModal';
import useInvoiceCalculator from '../../hooks/useInvoiceCalculator';
import { formatCurrency } from '../../utils/formatters';

const inputClass = 'w-full rounded-xl border border-slate-200 p-3';
const labels = { origin: 'Origin', freight: 'Navlun', delivery: 'Teslimat' };

// Mounted once per opening; all draft state is local to this modal.
const QuickExpenseModal = ({ operation, stage, onClose, onSaved }) => {
  const [invoice, setInvoice] = useState({
    type: 'Purchase', accountId: '', invoiceNumber: '', date: new Date().toLocaleDateString('en-CA'),
    currency: 'TRY', exchangeRate: 1, items: [], subTotal: 0, totalVat: 0, grandTotal: 0
  });
  const [account, setAccount] = useState(null);
  const [supplierType, setSupplierType] = useState('PARTNER');
  const [accountOpen, setAccountOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [itemIndex, setItemIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const setActiveInvoiceItems = useCallback((items) => setInvoice((prev) => ({ ...prev, items })), []);
  const setTotals = useCallback((totals) => setInvoice((prev) => ({ ...prev, ...totals })), []);
  const binding = { activeInvoice: invoice, setActiveInvoiceItems, setTotals };
  const { calculateRow, calculateAllTotals } = useInvoiceCalculator(binding);
  const field = (name) => (event) => setInvoice((prev) => ({ ...prev, [name]: event.target.value }));

  const selectProduct = (product) => {
    const sourceCurrency = product.purchaseCurrency || 'TRY';
    if (sourceCurrency !== invoice.currency && sourceCurrency !== 'TRY') {
      setError(`Bu ürünün alış fiyatı ${sourceCurrency}. Fatura para birimini ${sourceCurrency} seçin veya aynı para birimindeki bir ürün kullanın.`);
      return;
    }
    const rate = invoice.currency === 'TRY' ? 1 : Number(invoice.exchangeRate);
    if (!Number.isFinite(rate) || rate <= 0) { setError('Önce geçerli bir kur girin.'); return; }
    const row = calculateRow({
      productId: product._id, name: product.name, unit: product.unit || 'Adet', quantity: 1,
      unitPrice: sourceCurrency === invoice.currency ? product.purchasePrice : Math.round(product.purchasePrice / rate * 100) / 100,
      vatRate: product.purchaseVatRate ?? 20, isVatIncluded: Boolean(product.purchaseVatIncluded),
      discountValue: 0, discountType: 'percentage', description: product.description || ''
    });
    const items = [...invoice.items];
    if (itemIndex === null) items.push(row);
    else items[itemIndex] = row;
    setActiveInvoiceItems(items);
    calculateAllTotals(items);
    setProductOpen(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!invoice.accountId || !invoice.items.length) { setError('Tedarikçi seçin ve en az bir kalem ekleyin.'); return; }
    submitting.current = true;
    setSaving(true);
    setError('');
    try {
      await axios.post(`/api/operations/${operation._id}/stages/${stage}/expenses`, {
        ...invoice, operation: operation._id, operationStage: stage.toUpperCase(), type: 'Purchase',
        exchangeRate: invoice.currency === 'TRY' ? 1 : Number(invoice.exchangeRate)
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Masraf kaydedilemedi.');
      submitting.current = false;
      setSaving(false);
      return;
    }
    toast.success('Masraf faturası oluşturuldu.');
    onClose();
    await onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="quick-expense-title" className="max-h-[90vh] w-full max-w-6xl overflow-auto rounded-3xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between"><h2 id="quick-expense-title" className="text-xl font-black">Hızlı Masraf Ekle</h2><button type="button" disabled={saving} onClick={onClose}>Kapat</button></div>
        <p className="mb-5 rounded-xl bg-blue-50 p-3 font-bold">{operation.projectNo} · {labels[stage]} · Alış / Gider Faturası</p>
        <form onSubmit={submit} className="space-y-5">
          <fieldset disabled={saving} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <label>Tedarikçi türü<select className={inputClass} value={supplierType} onChange={(e) => setSupplierType(e.target.value)}><option value="PARTNER">Firma</option><option value="CUSTOMER">Müşteri / Cari</option></select></label>
              <button type="button" className="rounded-xl border p-3 md:col-span-2" onClick={() => setAccountOpen(true)}>{account ? `${account.name} (${account.code})` : 'Tedarikçi Cari Seç'}</button>
              <label>Belge / Fatura No<input required className={inputClass} value={invoice.invoiceNumber} onChange={field('invoiceNumber')} /></label>
              <label>Fatura Tarihi<input required type="date" className={inputClass} value={invoice.date} onChange={field('date')} /></label>
              <label>Para Birimi<select disabled={invoice.items.length > 0} className={inputClass} value={invoice.currency} onChange={(e) => setInvoice((prev) => ({ ...prev, currency: e.target.value, exchangeRate: e.target.value === 'TRY' ? 1 : '' }))}>{['TRY', 'USD', 'EUR', 'GBP'].map((c) => <option key={c}>{c}</option>)}</select></label>
              {invoice.currency !== 'TRY' && <label>Kur (1 {invoice.currency} = TL)<input required type="number" min="0.000001" step="any" className={inputClass} value={invoice.exchangeRate} onChange={field('exchangeRate')} /></label>}
            </div>
            <div className="overflow-x-auto"><div className="min-w-[850px]"><InvoiceItemsTable binding={binding} onOpenProductModal={(index = null) => { setItemIndex(index); setProductOpen(true); }} /></div></div>
            <div className="flex flex-wrap justify-end gap-6 font-bold"><span>Ara Toplam: {formatCurrency(invoice.subTotal, invoice.currency)}</span><span>KDV: {formatCurrency(invoice.totalVat, invoice.currency)}</span><span>Toplam: {formatCurrency(invoice.grandTotal, invoice.currency)}</span></div>
          </fieldset>
          {error && <p role="alert" className="text-red-600">{error}</p>}
          <button disabled={saving} className="rounded-xl bg-blue-600 px-6 py-3 font-bold text-white disabled:opacity-50">{saving ? 'Kaydediliyor...' : 'Masrafı Kaydet'}</button>
        </form>
        <AccountSelectModal isOpen={accountOpen} onClose={() => setAccountOpen(false)} accountType={supplierType} enableQuickCreate={false} title="Tedarikçi Cari Seçimi" onSelect={(selected) => { setAccount(selected); setInvoice((prev) => ({ ...prev, accountId: selected._id })); setAccountOpen(false); }} />
        <ProductSelectModal isOpen={productOpen} onClose={() => setProductOpen(false)} onSelect={selectProduct} />
      </div>
    </div>
  );
};

export default QuickExpenseModal;
